import React from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  SvgIcon
} from '@mui/material';
import {
  Remove as RemoveIcon,
  Add as AddIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { BookOpen as BookIcon } from '@opengov/react-capital-assets';

import { styles } from './Toolbar.styles';
import { ISetupPdfMessages } from '../../../../types/setupPDFTypes';

export type Mode = 'edit' | 'preview';
export const ZOOM_OPTIONS = [50, 75, 100, 125, 150];
interface ToolbarProps {
  messages: ISetupPdfMessages;
  zoom: number;
  zoomOptions?: number[];
  currentStory: number;
  totalStories: number;
  mode: Mode;
  selectedStoryId: string;
  isCreatingPdf: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomChange: (value: number) => void;
  onPrevStory?: () => void;
  onNextStory?: () => void;
  onModeChange: (mode: Mode) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  messages,
  zoom,
  zoomOptions = ZOOM_OPTIONS,
  currentStory,
  totalStories,
  mode,
  selectedStoryId,
  isCreatingPdf,
  onZoomIn,
  onZoomOut,
  onZoomChange,
  onPrevStory,
  onNextStory,
  onModeChange
}) => {
  const handleZoomChange = (event: SelectChangeEvent<number>) => {
    onZoomChange(Number(event.target.value));
  };
  const showSaveAndExitButton = selectedStoryId && onPrevStory && onNextStory
  return (
    <Box sx={styles.toolbar}>
      <IconButton disabled={zoom === zoomOptions[0]} onClick={onZoomOut} aria-label={messages.zoomOut}>
        <RemoveIcon sx={styles.iconButton} />
      </IconButton>

      <Box sx={styles.selectContainer}>
        <Select value={zoom} onChange={handleZoomChange} size="small" sx={styles.select}>
          {zoomOptions.map((val) => (
            <MenuItem key={val} value={val}>
              {val}%
            </MenuItem>
          ))}
        </Select>
      </Box>
      <IconButton
        disabled={zoom === zoomOptions[zoomOptions.length - 1]}
        onClick={onZoomIn}
        aria-label={messages.zoomIn}
      >
        <AddIcon sx={styles.iconButton} />
      </IconButton>

      {showSaveAndExitButton ? (
        <>
          <Divider orientation="vertical" sx={styles.divider} />
          <Box sx={styles.storyNavigation}>
            <Button
              color="secondary"
              disabled={currentStory === 1 || isCreatingPdf}
              startIcon={<ArrowBackIosNewIcon sx={styles.iconButton} />}
              onClick={onPrevStory}
              aria-label={messages.previousStory}
            >
              {messages.previous}
            </Button>
            <Button
              color="secondary"
              disabled={currentStory === totalStories || isCreatingPdf}
              endIcon={<ArrowForwardIosIcon sx={styles.iconButton} />}
              onClick={onNextStory}
              aria-label={messages.nextStory}
            >
              {messages.next}
            </Button>
          </Box>
        </>
      ) : null}

      <Divider orientation="vertical" sx={styles.divider} />

      <ToggleButtonGroup
        sx={styles.modeSwitch}
        color="primary"
        value={mode}
        exclusive
        onChange={(_, value) => {
          const selectedMode = value as Mode;
          if (selectedMode !== mode) {
            onModeChange(selectedMode);
          }
        }}
        aria-label={messages.storyMode}
      >
        <ToggleButton value="edit" sx={styles.toggleButton} size="small">
          <EditIcon sx={styles.toggleButtonIcon} />
          <Typography variant="h4">{messages.edit}</Typography>
        </ToggleButton>
        <ToggleButton value="preview" sx={styles.toggleButton} size="small">
          <SvgIcon component={BookIcon} inheritViewBox sx={styles.toggleButtonIcon} />
          <Typography variant="h4">{messages.preview}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default Toolbar;
